import React, { FC, Fragment, useState, useEffect, useMemo, useCallback } from 'react';
import { formatDeliveryScheduleDate } from 'utils/format';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import DeliverySchedulePageActions from 'components/delivery-schedule/delivery-schedule-page-actions.component';
import DeliveryScheduleAccordion from 'components/delivery-schedule/delivery-schedule-accordion.component';
import SendToClinicDialog from 'components/orders/order-details/send-to-clinic-dialog.component';
import { Box, Checkbox, FormControlLabel, Paper } from '@mui/material';
import BatchActionBar, { BATCH_SELECTION_LIMIT } from 'components/orders/shared/batch-action-bar.component';
import { getBatchOrderActions, isBatchOrderActionDisabled } from 'components/orders/shared/batch-order-actions';
import { useBatchOrderActions } from 'components/orders/shared/use-batch-order-actions';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';
import {
    DeliverySchedule,
    DeliveryScheduleFilters,
    DeliveryScheduleFinishedFilter,
    DeliveryScheduleInput,
} from 'models/delivery-schedule';
import { useLazyQuery, useMutation } from '@apollo/client';
import Loader from 'components/shared/loader/loader.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { DeliveryScheduleTemplate, removeFromScheduleTemplate } from 'apollo/templates/delivery-schedule';
import { Order } from 'models/order';
import DeliveryScheduleLateOrders from 'components/delivery-schedule/delivery-schedule-late-orders.component';
import DeliveryScheduleNoDeadlineOrders from 'components/delivery-schedule/delivery-schedule-no-deadline-orders.component';
import ChangeInternalDeliveryDateDialog from 'components/orders/order-details/change-internal-delivery-date-dialog.component';
import { Helmet } from 'react-helmet-async';
import ChangeExternalDeliveryDateDialog from 'components/orders/order-details/change-external-delivery-date-dialog.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';

type DayOverviewProps = {
    isExternal: boolean;
};

const FILTER_STORAGE_KEY = 'deliveryScheduleFilter';
const PLANNED_PAGE_SIZE = 30;

const DEFAULT_FILTERS: DeliveryScheduleFilters = {
    clinic: null,
    department: null,
    finishedFilter: DeliveryScheduleFinishedFilter.NOT_FINISHED,
};

// Clinic and department survive a reload; the state filter always starts at "Not finished".
const readStoredFilters = (): DeliveryScheduleFilters => {
    try {
        const stored = JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) || '{}');
        return { ...DEFAULT_FILTERS, clinic: stored.clinic ?? null, department: stored.department ?? null };
    } catch {
        return DEFAULT_FILTERS;
    }
};

const storeFilters = (filters: DeliveryScheduleFilters) => {
    localStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({ clinic: filters.clinic, department: filters.department }),
    );
};

const buildDeliveryScheduleInput = (
    filters: DeliveryScheduleFilters,
    date: Date | null,
    isExternal: boolean,
): DeliveryScheduleInput => ({
    date,
    clinicId: filters.clinic?._id,
    departmentId: filters.department?._id,
    finishedFilter: filters.finishedFilter,
    isExternal,
});

const DayOverview: FC<DayOverviewProps> = ({ isExternal }: DayOverviewProps) => {
    const { t: translate } = useTranslation(['delivery-schedule', 'transport']);
    const [filters, setFilters] = useState<DeliveryScheduleFilters>(readStoredFilters);
    const [date, setDate] = useState<Date | null>(new Date());
    const [deliverySchedule, setDeliverySchedule] = useState<DeliverySchedule>({} as DeliverySchedule);
    const [openSentByLabDialog, setOpenSentByLabDialog] = useState(false);
    const [openRemoveFromScheduleDialog, setOpenRemoveFromScheduleDialog] = useState(false);
    const [changeInternalDeliveryDateDialogOpen, setChangeInternalDeliveryDateDialogOpen] = useState(false);
    const [changeExternalDeliveryDateDialogOpen, setChangeExternalDeliveryDateDialogOpen] = useState(false);
    const [order, setOrder] = useState<Order>({} as Order);
    const [showLateOrders, setShowLateOrders] = useState(false);
    const [showNoDeadlineOrders, setShowNoDeadlineOrders] = useState(false);
    const [plannedOffset, setPlannedOffset] = useState(0);
    const [plannedHasMore, setPlannedHasMore] = useState(false);
    const [plannedLoadingMore, setPlannedLoadingMore] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const { t: translateOrders } = useTranslation(['orders']);
    const currentUser = currentUserVar();

    // DD-3463: batch selection across the planned and finished lists of the day.
    const [batchActionSelected, setBatchActionSelected] = useState(false);
    const [checkedOrders, setCheckedOrders] = useState<Map<string, Order>>(new Map());
    const checkedOrderIds = useMemo(() => new Set(checkedOrders.keys()), [checkedOrders]);
    const isAtLimit = checkedOrders.size >= BATCH_SELECTION_LIMIT;
    const handleCheckedOrders = useCallback((order: Order, checked: boolean) => {
        setCheckedOrders((current) => {
            if (checked && current.size >= BATCH_SELECTION_LIMIT) {
                return current;
            }
            const updated = new Map(current);
            if (checked) {
                updated.set(order._id as string, order);
            } else {
                updated.delete(order._id as string);
            }
            return updated;
        });
    }, []);
    const clearSelection = () => setCheckedOrders(new Map());
    const batchOrderActions = getBatchOrderActions(translateOrders).map((action) => ({
        ...action,
        disabled: isBatchOrderActionDisabled(action.type, {
            isDentist: permissions.isDentist(currentUser.type),
            isExternalTechnician: permissions.isExternalTechnician(currentUser.role),
            isElevatedExternalUser: permissions.isElevatedExternalUser(currentUser.role),
        }),
    }));

    const [getDeliveryScheduleOrders] = useLazyQuery(DeliveryScheduleTemplate, {
        fetchPolicy: 'no-cache',
        onError(error) {
            showBoundary(error);
        },
    });

    // Paginate plannedOrders with a "load more" button. Reset loads replace the list; "show more"
    // appends the next page. Kept in component state (no-cache) to avoid Apollo merge complexity
    // on the nested plannedOrders field.
    const loadPlanned = async (deliveryScheduleInput: DeliveryScheduleInput, offset: number, append: boolean) => {
        if (append) {
            setPlannedLoadingMore(true);
        } else {
            setInitialLoading(true);
        }
        try {
            const result = await getDeliveryScheduleOrders({
                variables: { deliveryScheduleInput, limit: PLANNED_PAGE_SIZE, offset },
            });
            const schedule = result.data?.deliverySchedule;
            if (schedule) {
                const fetched: Order[] = schedule.plannedOrders || [];
                setDeliverySchedule((prev) => ({
                    ...schedule,
                    plannedOrders: append ? [...(prev.plannedOrders || []), ...fetched] : fetched,
                }));
                setPlannedOffset(offset + fetched.length);
                setPlannedHasMore(fetched.length === PLANNED_PAGE_SIZE);
            }
        } finally {
            if (append) {
                setPlannedLoadingMore(false);
            } else {
                setInitialLoading(false);
            }
        }
    };

    const reloadPlanned = (nextFilters: DeliveryScheduleFilters, nextDate: Date | null) =>
        loadPlanned(buildDeliveryScheduleInput(nextFilters, nextDate, isExternal), 0, false);

    const handleShowMorePlanned = () => {
        loadPlanned(buildDeliveryScheduleInput(filters, date, isExternal), plannedOffset, true);
    };

    const [removeFromSchedule, { loading: removeFromScheduleLoading, error: removeFromScheduleError }] = useMutation(
        removeFromScheduleTemplate,
        {
            onCompleted: ({ removeFromSchedule: order }) => {
                const ordersArr = JSON.parse(JSON.stringify(deliverySchedule.plannedOrders || []));
                const index = ordersArr.findIndex((ord: Order) => ord._id === order._id);
                if (index !== -1) {
                    ordersArr.splice(index, 1);
                    setDeliverySchedule({ ...deliverySchedule, plannedOrders: ordersArr });
                }
                setOpenRemoveFromScheduleDialog(false);
            },
        },
    );

    useEffect(() => {
        reloadPlanned(filters, date);
    }, []);

    const handleFiltersChange = async (nextFilters: DeliveryScheduleFilters) => {
        setFilters(nextFilters);
        storeFilters(nextFilters);
        await reloadPlanned(nextFilters, date);
    };

    const handleDateFilterChange = async (nextDate: Date | null) => {
        setDate(nextDate);
        await reloadPlanned(filters, nextDate);
    };

    const handleSentByClinicDialog = (order: Order) => {
        setOrder(order);
        setOpenSentByLabDialog(true);
    };

    const handleRemoveFromScheduleDialog = (order: Order) => {
        setOrder(order);
        setOpenRemoveFromScheduleDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenSentByLabDialog(false);
        setOpenRemoveFromScheduleDialog(false);
        setChangeInternalDeliveryDateDialogOpen(false);
        setChangeExternalDeliveryDateDialogOpen(false);
        setOrder({} as Order);
    };

    const fetchData = async () => {
        await reloadPlanned(filters, date);
    };

    const { openBatchOrderAction, dialogs: batchOrderActionDialogs } = useBatchOrderActions({
        onCompleted: () => {
            setBatchActionSelected(false);
            clearSelection();
            fetchData();
        },
    });

    const handleLateOrders = () => {
        setShowLateOrders(true);
    };

    const handleNoDeadline = () => {
        setShowNoDeadlineOrders(true);
    };

    const handleInternalDeliveryDate = (order: Order) => {
        setOrder(order);
        setChangeInternalDeliveryDateDialogOpen(true);
    };

    const handleExternalDeliveryDate = (order: Order) => {
        setOrder(order);
        setChangeExternalDeliveryDateDialogOpen(true);
    };

    const getDateText = (date: Date) => {
        const text = formatDeliveryScheduleDate(date, 'date');
        if (text === 'TODAY' || text === 'TOMORROW') {
            return translate(`transport:${text}`);
        }
        return text;
    };

    const getTableTitle = (): string => {
        switch (filters.finishedFilter) {
            case DeliveryScheduleFinishedFilter.FINISHED:
                return translate('delivery-schedule:FINISHED');
            case DeliveryScheduleFinishedFilter.BOTH:
                return translate('delivery-schedule:PLANNED_AND_FINISHED_THIS_DAY');
            default:
                return translate('delivery-schedule:PLANNED_THIS_DAY');
        }
    };

    const isFinishedOnly = filters.finishedFilter === DeliveryScheduleFinishedFilter.FINISHED;
    // DD-3546: the Print menu is offered for every state filter; the list PDF honours clinic + department.
    const isPrintAvailable = !isExternal;
    // The list PDF is always the planned list; offering it on Finished/Both would print something else.
    const isListPrintAvailable = filters.finishedFilter === DeliveryScheduleFinishedFilter.NOT_FINISHED;

    return (
        <Fragment>
            <Helmet>
                <title>{translate('orders:DELIVERY_SCHEDULE')} | Denthub</title>
            </Helmet>
            {removeFromScheduleError ? (
                <AlertSnackbar type="error" message={removeFromScheduleError?.message || ''} />
            ) : undefined}
            {showLateOrders ? (
                <DeliveryScheduleLateOrders onBackArrow={() => setShowLateOrders(false)} isExternal={isExternal} />
            ) : undefined}

            {showNoDeadlineOrders && (
                <DeliveryScheduleNoDeadlineOrders
                    onBackArrow={() => setShowNoDeadlineOrders(false)}
                    isExternal={isExternal}
                />
            )}

            {!showLateOrders && !showNoDeadlineOrders && (
                <Paper variant="outlined" sx={{ padding: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <PageTitle headingStyle="h1" title={getDateText(date as Date)} />
                        <FormControlLabel
                            checked={batchActionSelected}
                            onChange={() => {
                                setBatchActionSelected(!batchActionSelected);
                                clearSelection();
                            }}
                            control={<Checkbox />}
                            label={translateOrders('orders:BATCH_ACTION')}
                            data-test="delivery-schedule.batch-action-checkbox"
                        />
                    </Box>
                    <DeliverySchedulePageActions
                        filters={filters}
                        onFiltersChange={handleFiltersChange}
                        selectedDate={date}
                        onDateFilterChange={handleDateFilterChange}
                        lateOrdersCount={deliverySchedule.lateOrdersCount}
                        noDeadlineOrdersCount={deliverySchedule.noDeadlineOrdersCount}
                        onLateOrdersShow={handleLateOrders}
                        onNoDeadlineShow={handleNoDeadline}
                        isExternal={isExternal}
                    />
                    {!initialLoading ? (
                        <Box mt={2}>
                            {removeFromScheduleLoading ? <Loader /> : undefined}
                            <DeliveryScheduleAccordion
                                date={date as Date}
                                clinicId={filters.clinic?._id}
                                departmentId={filters.department?._id}
                                orders={deliverySchedule.plannedOrders || []}
                                style={isFinishedOnly ? 'completed' : 'normal'}
                                title={getTableTitle()}
                                onSentByClinicDialog={handleSentByClinicDialog}
                                onRemoveFromScheduleDialog={handleRemoveFromScheduleDialog}
                                onChangeInternalDeliveryDate={handleInternalDeliveryDate}
                                onChangeExternalDeliveryDate={handleExternalDeliveryDate}
                                defaultExpanded={true}
                                isExternal={isExternal}
                                isPrintAvailable={isPrintAvailable}
                                isListPrintAvailable={isListPrintAvailable}
                                onShowMore={handleShowMorePlanned}
                                showMoreVisible={plannedHasMore}
                                loadingMore={plannedLoadingMore}
                                batchActionSelected={batchActionSelected}
                                checkedOrderIds={checkedOrderIds}
                                isAtLimit={isAtLimit}
                                onHandleCheckedOrders={handleCheckedOrders}
                            />
                            {batchActionSelected && checkedOrders.size ? (
                                <BatchActionBar
                                    selectedCount={checkedOrders.size}
                                    limit={BATCH_SELECTION_LIMIT}
                                    actions={batchOrderActions}
                                    onActionClick={(type) =>
                                        openBatchOrderAction(type, Array.from(checkedOrders.keys()))
                                    }
                                    onClearAll={clearSelection}
                                />
                            ) : undefined}
                        </Box>
                    ) : (
                        <Loader />
                    )}
                </Paper>
            )}

            {openSentByLabDialog ? (
                <SendToClinicDialog
                    onUpdate={fetchData}
                    open={openSentByLabDialog}
                    onClose={handleCloseDialog}
                    orderId={order._id}
                />
            ) : undefined}

            <ConfirmDialog
                open={openRemoveFromScheduleDialog}
                handleClose={handleCloseDialog}
                dialogContent={translate('delivery-schedule:REMOVE_EXTERNAL_DEADLINE_DESC')}
                handleConfirm={() => removeFromSchedule({ variables: { orderId: order._id } })}
            />

            <ChangeInternalDeliveryDateDialog
                onUpdate={fetchData}
                open={changeInternalDeliveryDateDialogOpen}
                onClose={handleCloseDialog}
                order={order}
            />
            {order.deliveryDetails ? (
                <ChangeExternalDeliveryDateDialog
                    onUpdate={fetchData}
                    open={changeExternalDeliveryDateDialogOpen}
                    onClose={handleCloseDialog}
                    order={order}
                />
            ) : undefined}
            {batchOrderActionDialogs}
            {/* DD-3592: the recent-batches button sits in the toolbar of
                order-list.component.tsx, the only place this component renders; mounting
                RecentDeliveryNoteBatches here too would show a duplicate button. */}
        </Fragment>
    );
};

export default DayOverview;
